<template>
<!--  hack for loading spinner, with-loader gives relative class, which prevents dropdowns to be seen-->
  <component :is="isLoading ? 'with-loader' : 'div'"
             class="flex items-center"
             :class="{ 'h-full w-1/2': isLoading }"
             :loading="isLoading"
             id="filter-menu"
             :trigger-loader-on-mount="true"
  >
    <t-dropdown class="filter-menu-element"
                variant="filters"
                :show.sync="showDropdown"
                v-on-clickaway="closeFilterMenu"
    >
      <div slot="trigger" slot-scope="{ blurHandler, mousedownHandler, isShown }">
        <t-button variant="transparent"
                  class="test-filter-menu flex items-center"
                  :class="isShown ? 'bg-primary-light' : 'white'"
                  @blur="blurHandler"
                  @mousedown="mousedownHandler">
          <filters-applied-icon v-if="areAdditionalFiltersApplied"
                                :class="isShown ? 'text-primary-focus' : 'text-gray-dark'"/>
          <no-filters-icon v-else :class="isShown ? 'text-primary-focus' : 'text-gray-dark'"/>
        </t-button>
      </div>

      <filter-menu-dropdown :selected-item="selectedItem"
                            :use-logical-bots="useLogicalBots"
                            :show-request-outcomes="showRequestOutcomes"
                            :show-assistance-resolutions="showAssistanceResolutions"
                            :show-channels="showChannels"
                            :show-markets="showMarkets"
                            :show-granularity="showGranularity"
                            :filter-values="filterValues"
                            :is-loading="isLoading"
                            :show-answer="showAnswer"
                            @select-item="onSelectItem"
                            @filter-change="onFilterChange"
                            @submit-filters="fireFilterChange"
                            @reset-filters="onReset"
      />
    </t-dropdown>
    <filter-bar @badge-click="onBadgeClick"
                @badge-remove="onBadgeRemove"
                :use-logical-bots="useLogicalBots"
                :show-markets="showMarkets"
                :show-request-outcomes="showRequestOutcomes"
                :show-assistance-resolutions="showAssistanceResolutions"
                :show-channels="showChannels"
                :show-granularity="showGranularity"
                :show-answer="showAnswer"
    />
  </component>
</template>

<script>
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import NoFiltersIcon from '@/assets/images/icons/NoFiltersIcon'
import FiltersAppliedIcon from '@/assets/images/icons/FiltersAppliedIcon'
import FilterMenuDropdown from './FilterMenuDropdown'
import FilterBar from './FilterBar.vue'
import { GRANULARITY_REQUEST, FILTER_ITEMS } from '@/utils/filter.constants'
import moment from 'moment-timezone'
import dissoc from 'ramda/src/dissoc'
import mergeRight from 'ramda/src/mergeRight'
import keys from 'ramda/src/keys'
import { directive as onClickaway } from 'vue-clickaway'
import equals from 'ramda/src/equals'
import channelModule from '@/store/modules/channel.module'
import outcomeModule from '@/store/modules/outcome.module'
import datetimeUtils from '@/utils/datetime.utils'
import WithLoader from '@/components/common/WithLoader'
import { getMarketsForLogicalBots, getLogicalBotId } from '@/utils/market.utils'

export default {
  name: 'FilterMenu',
  components: { WithLoader, NoFiltersIcon, FiltersAppliedIcon, FilterMenuDropdown, FilterBar },

  directives: {
    onClickaway: onClickaway
  },

  props: {
    showRequestOutcomes: {
      type: Boolean,
      default: false
    },
    showAssistanceResolutions: {
      type: Boolean,
      default: false
    },
    showChannels: {
      type: Boolean,
      default: false
    },
    showMarkets: {
      type: Boolean,
      default: false
    },
    showGranularity: {
      type: Boolean,
      default: false
    },
    showAnswer: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    useLogicalBots: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showDropdown: false,
      selectedItem: null,
      filterValues: {}
    }
  },

  computed: {
    bot: analyticsFilterModule.getBot,
    channelOptions: channelModule.getChannelList,

    routeQueryChannels() {
      const { channels } = this.$route.query
      if (channels === '') {
        return []
      }
      return channels ? channels.split(',') : null
    },

    routeQueryOutcomes() {
      const { requestOutcomes } = this.$route.query
      if (requestOutcomes === '') {
        return []
      }
      return requestOutcomes ? requestOutcomes.split(',') : null
    },

    routeQueryAssistanceResolutions() {
      const { assistanceResolutions } = this.$route.query
      if (assistanceResolutions === '') {
        return []
      }
      return assistanceResolutions ? assistanceResolutions.split(',') : null
    },

    routeQueryMarkets() {
      const { markets } = this.$route.query
      if (markets === '') {
        return []
      }
      return markets ? markets.split(',') : null
    },

    routeQueryBots() {
      const { bots } = this.$route.query
      if (bots === '') {
        return []
      }
      return bots ? bots.split(',') : null
    },

    // BOTS === null means "the anchor bot only" (default), an array is an explicit selection
    selectedLogicalBotIds() {
      const selectedBots = this.filterValues[FILTER_ITEMS.BOTS]
      if (selectedBots !== null && selectedBots !== undefined) return selectedBots

      const anchorBot = this.filterValues[FILTER_ITEMS.BOT] || this.bot
      const anchorLogicalBotId = getLogicalBotId(anchorBot)
      return anchorLogicalBotId ? [anchorLogicalBotId] : []
    },

    marketOptions() {
      return getMarketsForLogicalBots(this.selectedLogicalBotIds)
    },

    areAdditionalFiltersApplied() {
      const {
        requestOutcomes,
        channels,
        assistanceResolutions,
        markets
      } = this.filterValues
      const channelsApplied = this.showChannels && channels && channels.length !== this.channelOptions.length
      const marketsApplied = this.showMarkets &&
        this.marketOptions.length > 0 &&
        markets !== null &&
        markets.length !== this.marketOptions.length
      const botsApplied = this.useLogicalBots && !!this.filterValues[FILTER_ITEMS.BOTS]
      const requestOutcomesApplied = this.showRequestOutcomes && requestOutcomes
      const assistanceResolutionsApplied = this.showAssistanceResolutions && assistanceResolutions
      return requestOutcomesApplied || channelsApplied || assistanceResolutionsApplied || marketsApplied || botsApplied
    }
  },

  created() {
    outcomeModule.initOutcomeList()
    outcomeModule.initResolutionList()
  },

  watch: {
    showDropdown(value) {
      if (!value) {
        this.selectedItem = null
      }
    },

    // HACK: the bot value is being set asynchronously by the parent component
    // this will be removed when we change the Conversations route to /conversations
    bot(bot) {
      this.filterValues[FILTER_ITEMS.BOT] = bot
      if (this.showMarkets) {
        this.normalizeMarketSelection()
      }
    },

    marketOptions(options) {
      if (this.showMarkets && options.length) {
        this.normalizeMarketSelection()
      }
    }
  },

  methods: {
    getLastWeek() {
      return datetimeUtils.toBorderMoment(moment(), 'day').subtract(7, 'days').format()
    },

    getToday() {
      return datetimeUtils.toBorderMoment(moment(), 'day', 'endOf').format()
    },

    datesAreValid(dateFrom, dateTo) {
      const isValidFormat = dateString => moment(dateString).isValid()
      return isValidFormat(dateFrom) && isValidFormat(dateTo) && moment(dateTo).isSameOrAfter(dateFrom)
    },

    setDatesToRouteQuery(dateFrom, dateTo) {
      // avoid exception NavigationDuplicated: Avoided redundant navigation to current location...
      if (this.$route.query.dateFrom === dateFrom && this.$route.query.dateTo === dateTo) return

      this.$router.replace({ query: { ...this.$route.query, dateFrom, dateTo } })
    },

    setChannelsRouteQuery(channels) {
      if (equals(channels, this.routeQueryChannels)) return

      const areChannelsSet = channels !== null
      const query = areChannelsSet ?
        { ...this.$route.query, channels: channels?.join(',') } :
        dissoc('channels', this.$route.query)

      this.$router.replace({ query })
    },

    setBotsRouteQuery(bots) {
      const anchorLogicalBotId = getLogicalBotId(this.filterValues[FILTER_ITEMS.BOT] || this.bot)
      let desiredBots = null
      if (bots !== null && bots !== undefined) {
        desiredBots = bots
      } else if (anchorLogicalBotId) {
        desiredBots = [anchorLogicalBotId]
      }

      if (equals(desiredBots, this.routeQueryBots)) return

      let query = dissoc('bots', this.$route.query)
      if (desiredBots) {
        query = { ...this.$route.query, bots: desiredBots.join(',') }
      }

      this.$router.replace({ query })
    },

    setMarketsRouteQuery(markets) {
      if (equals(markets, this.routeQueryMarkets)) return

      const areMarketsSet = markets !== null
      const query = areMarketsSet ?
        { ...this.$route.query, markets: markets?.join(',') } :
        dissoc('markets', this.$route.query)

      this.$router.replace({ query })
    },

    setGranularityRouteQuery(granularity) {
      if (granularity === this.$route.query.granularity) return

      this.$router.replace({ query: { ...this.$route.query, granularity } })
    },

    setRequestOutcomesRouteQuery(outcomes) {
      if (equals(outcomes, this.routeQueryOutcomes)) return

      const areOutcomesSet = outcomes !== null
      const query = areOutcomesSet ?
        { ...this.$route.query, requestOutcomes: outcomes?.join(',') } :
        dissoc('requestOutcomes', this.$route.query)

      this.$router.replace({ query })
    },

    setAssistanceResolutionsRouteQuery(assistanceResolutions) {
      if (equals(assistanceResolutions, this.routeQueryAssistanceResolutions)) return

      const areOutcomesSet = assistanceResolutions !== null
      const query = areOutcomesSet ?
        { ...this.$route.query, assistanceResolutions: assistanceResolutions?.join(',') } :
        dissoc('assistanceResolutions', this.$route.query)

      this.$router.replace({ query })
    },

    setAnswerIdRouteQuery(answerId) {
      if (answerId === this.$route.query.answerId) return
      if (!answerId && !this.$route.query.answerId) return

      const query = answerId ? { ...this.$route.query, answerId } : dissoc('answerId', this.$route.query)
      this.$router.replace({ query })
    },

    processDateFrom(dateFrom) {
      return dateFrom.length === 10 ? datetimeUtils.toBorderMoment(dateFrom, 'day').format() : dateFrom
    },

    processDateTo(dateTo) {
      return dateTo.length === 10 ? datetimeUtils.toBorderMoment(dateTo, 'day', 'endOf').format() : dateTo
    },

    processDates(dateFrom, dateTo) {
      const formattedDateFrom = this.processDateFrom(dateFrom)
      const formattedDateTo = this.processDateTo(dateTo)
      analyticsFilterModule.setDateFrom(formattedDateFrom)
      analyticsFilterModule.setDateTo(formattedDateTo)
      this.setDatesToRouteQuery(formattedDateFrom, formattedDateTo)
    },

    normalizeMarketSelection() {
      const marketOptions = this.marketOptions
      if (!marketOptions.length) return

      let selectedMarkets = this.filterValues[FILTER_ITEMS.MARKETS]
      // null means "all markets" (intentional clear / default) — do not restore from store
      if (selectedMarkets === null) return
      // undefined means not initialized yet (e.g. market options loaded after mount)
      if (selectedMarkets === undefined) {
        selectedMarkets = analyticsFilterModule.getMarkets()
        if (selectedMarkets === null || selectedMarkets === undefined) return
      }

      const marketIds = marketOptions.map(({ id }) => id)
      const normalizedMarkets = selectedMarkets.filter(marketId => marketIds.includes(marketId))
      this.filterValues[FILTER_ITEMS.MARKETS] = normalizedMarkets.length === marketIds.length ? null : normalizedMarkets
    },

    closeFilterMenu(event) {
      const closest = (selector) => event?.target?.closest(selector)
      if (closest('.filter-menu-element') || closest('.bot-selector-item')) return

      this.showDropdown = false
    },

    onBadgeClick(type) {
      if (this.selectedItem === type) {
        this.showDropdown = false
        this.selectedItem = null
      } else {
        this.showDropdown = true
        this.selectedItem = type
      }
    },

    onBadgeRemove(filter) {
      this.onFilterChange(filter)
      this.fireFilterChange()
    },

    onSelectItem(type) {
      this.selectedItem = type
    },

    onFilterChange(filter) {
      this.filterValues = mergeRight(this.filterValues, filter)
      const dateFrom = this.processDateFrom(this.filterValues[FILTER_ITEMS.DATE_FROM])
      const dateTo = this.processDateTo(this.filterValues[FILTER_ITEMS.DATE_TO])
      this.filterValues[FILTER_ITEMS.DATE_FROM] = dateFrom
      this.filterValues[FILTER_ITEMS.DATE_TO] = dateTo

      // make sure dateFrom is never after dateTo
      if (moment(dateFrom).isAfter(moment(dateTo))) {
        const key = keys(filter)[0]
        if (key === FILTER_ITEMS.DATE_TO) {
          this.filterValues[FILTER_ITEMS.DATE_FROM] = datetimeUtils.toBorderMoment(dateTo, 'day').format()
        } else {
          this.filterValues[FILTER_ITEMS.DATE_TO] = datetimeUtils.toBorderMoment(dateFrom, 'day', 'endOf').format()
        }
      }

      if (!this.filterValues[FILTER_ITEMS.BOT]) {
        this.filterValues[FILTER_ITEMS.BOT] = analyticsFilterModule.getBot()
      }

      const changedKey = keys(filter)[0]
      if (changedKey === FILTER_ITEMS.BOT) {
        this.filterValues[FILTER_ITEMS.BOTS] = null
        this.filterValues[FILTER_ITEMS.MARKETS] = null
        this.filterValues[FILTER_ITEMS.CHANNELS] = null
      }

      // changing the bot selection keeps the market narrowing (normalized below) but resets channels
      if (changedKey === FILTER_ITEMS.BOTS || changedKey === FILTER_ITEMS.MARKETS) {
        this.filterValues[FILTER_ITEMS.CHANNELS] = null
      }

      if (this.showMarkets) {
        this.normalizeMarketSelection()
      }
    },

    onReset() {
      this.filterValues = {
        [FILTER_ITEMS.BOT]: analyticsFilterModule.getBot(),
        [FILTER_ITEMS.BOTS]: null,
        [FILTER_ITEMS.DATE_FROM]: this.getLastWeek(),
        [FILTER_ITEMS.DATE_TO]: this.getToday(),
        [FILTER_ITEMS.GRANULARITY]: GRANULARITY_REQUEST,
        [FILTER_ITEMS.REQUEST_OUTCOME]: null,
        [FILTER_ITEMS.ASSISTANCE_RESOLUTIONS]: null,
        [FILTER_ITEMS.CHANNELS]: null,
        [FILTER_ITEMS.MARKETS]: null,
        [FILTER_ITEMS.ANSWER_ID]: null
      }
      this.fireFilterChange()
    },

    fireFilterChange() {
      const {
        dateFrom,
        dateTo,
        requestOutcomes,
        assistanceResolutions,
        channels,
        markets,
        granularity,
        answerId
      } = this.filterValues
      this.setDatesToRouteQuery(dateFrom, dateTo)

      if (this.showGranularity) {
        this.setGranularityRouteQuery(granularity)
      }

      if (this.showChannels) {
        this.setChannelsRouteQuery(channels)
      }

      if (this.useLogicalBots) {
        this.setBotsRouteQuery(this.filterValues[FILTER_ITEMS.BOTS])
      }

      if (this.showMarkets) {
        this.setMarketsRouteQuery(markets)
      }

      if (this.showRequestOutcomes) {
        this.setRequestOutcomesRouteQuery(requestOutcomes)
      }

      if (this.showAssistanceResolutions) {
        this.setAssistanceResolutionsRouteQuery(assistanceResolutions)
      }

      if (this.showAnswer) {
        this.setAnswerIdRouteQuery(answerId)
      }

      this.$emit('filter-change', this.filterValues)
      this.showDropdown = false
    },

    initFilterValues() {
      this.filterValues[FILTER_ITEMS.BOT] = analyticsFilterModule.getBot()
      this.filterValues[FILTER_ITEMS.DATE_FROM] = analyticsFilterModule.getDateFrom()
      this.filterValues[FILTER_ITEMS.DATE_TO] = analyticsFilterModule.getDateTo()

      if (this.useLogicalBots) {
        this.filterValues[FILTER_ITEMS.BOTS] = analyticsFilterModule.getLogicalBots()
      }

      if (this.showGranularity) {
        this.filterValues[FILTER_ITEMS.GRANULARITY] = analyticsFilterModule.getGranularity()
      }

      if (this.showChannels) {
        this.filterValues[FILTER_ITEMS.CHANNELS] = analyticsFilterModule.getChannels()
      }

      if (this.showMarkets) {
        this.filterValues[FILTER_ITEMS.MARKETS] = analyticsFilterModule.getMarkets()
      }

      if (this.showRequestOutcomes) {
        this.filterValues[FILTER_ITEMS.REQUEST_OUTCOME] = analyticsFilterModule.getRequestOutcomes()
      }

      if (this.showAssistanceResolutions) {
        this.filterValues[FILTER_ITEMS.ASSISTANCE_RESOLUTIONS] = analyticsFilterModule.getAssistanceResolutions()
      }

      if (this.showAnswer) {
        this.filterValues[FILTER_ITEMS.ANSWER_ID] = analyticsFilterModule.getAnswerId()
      }

      if (this.showMarkets) {
        this.normalizeMarketSelection()
      }
    }
  },

  beforeMount() {
    const routeDateFrom = this.$route.query.dateFrom
    const routeDateTo = this.$route.query.dateTo
    if (routeDateFrom && routeDateTo) {
      if (this.datesAreValid(routeDateFrom, routeDateTo)) {
        this.processDates(routeDateFrom, routeDateTo)
      } else {
        const lastWeek = this.getLastWeek()
        const today = this.getToday()
        this.processDates(lastWeek, today)
      }
    }

    const dateFrom = analyticsFilterModule.getDateFrom()
    const dateTo = analyticsFilterModule.getDateTo()
    if (dateFrom && dateTo) {
      this.setDatesToRouteQuery(dateFrom, dateTo)
    } else {
      const lastWeek = this.getLastWeek()
      const today = this.getToday()
      this.processDates(lastWeek, today)
    }

    if (this.showGranularity) {
      const granularity = this.$route.query.granularity || GRANULARITY_REQUEST
      analyticsFilterModule.setGranularity(granularity)
      this.setGranularityRouteQuery(granularity)
    }

    if (this.showChannels) {
      // Sidebar nav between Analytics/History drops non-bots query params; restore from store.
      if (typeof this.$route.query.channels !== 'undefined') {
        analyticsFilterModule.setChannels(this.routeQueryChannels)
      } else {
        const storedChannels = analyticsFilterModule.getChannels()
        if (storedChannels !== null && storedChannels !== undefined) {
          this.setChannelsRouteQuery(storedChannels)
        }
      }
    }

    if (this.useLogicalBots) {
      const routeBots = this.routeQueryBots
      // Keep "anchor-only" semantics in the store when exactly one logical bot is present.
      // Store: `null` === anchor-only, Array === explicit selection (possibly empty).
      if (routeBots && routeBots.length === 1) {
        analyticsFilterModule.setLogicalBots(null)
      } else {
        analyticsFilterModule.setLogicalBots(routeBots)
      }
    }

    if (this.showMarkets) {
      // Sidebar nav between Analytics/History drops non-bots query params; restore from store.
      if (typeof this.$route.query.markets !== 'undefined') {
        analyticsFilterModule.setMarkets(this.routeQueryMarkets)
      } else {
        const storedMarkets = analyticsFilterModule.getMarkets()
        if (storedMarkets !== null && storedMarkets !== undefined) {
          this.setMarketsRouteQuery(storedMarkets)
        }
      }
    }

    if (this.showRequestOutcomes) {
      if (typeof this.$route.query.requestOutcomes !== 'undefined') {
        analyticsFilterModule.setRequestOutcomes(this.routeQueryOutcomes)
      } else {
        const storedOutcomes = analyticsFilterModule.getRequestOutcomes()
        if (storedOutcomes !== null && storedOutcomes !== undefined) {
          this.setRequestOutcomesRouteQuery(storedOutcomes)
        }
      }
    }

    if (this.showAssistanceResolutions) {
      if (typeof this.$route.query.assistanceResolutions !== 'undefined') {
        analyticsFilterModule.setAssistanceResolutions(this.routeQueryAssistanceResolutions)
      } else {
        const storedResolutions = analyticsFilterModule.getAssistanceResolutions()
        if (storedResolutions !== null && storedResolutions !== undefined) {
          this.setAssistanceResolutionsRouteQuery(storedResolutions)
        }
      }
    }

    if (this.showAnswer) {
      if (typeof this.$route.query.answerId !== 'undefined') {
        analyticsFilterModule.setAnswerId(this.$route.query.answerId)
      } else {
        const storedAnswerId = analyticsFilterModule.getAnswerId()
        if (storedAnswerId) {
          this.setAnswerIdRouteQuery(storedAnswerId)
        }
      }
    }

    this.initFilterValues()
  }
}
</script>
